import { DialogComponent } from '@theme/dialog';

const STORAGE_KEY = 'teddilab_localization_preference';
const EXPIRY_DAYS = 365;
const IP_API_URL = 'https://ipapi.co/json/';

/**
 * A popup component for country/currency selection that appears on first visit.
 * Uses IP geolocation to recommend a country and remembers user preference.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element
 * @property {HTMLElement} recommendation - The recommendation section
 * @property {HTMLElement} detectedCountryName - Shows detected country name
 * @property {HTMLFormElement} form - The localization form
 * @property {HTMLUListElement} countryList - The country list container
 * @property {HTMLElement[]} countryItems - All country list items
 * @property {HTMLInputElement} countryInput - Hidden input for country code
 * @property {HTMLInputElement} searchInput - The country search input
 * @property {HTMLElement} noResults - No results message
 * @property {HTMLButtonElement} confirmButton - The confirm/continue button
 *
 * @extends DialogComponent
 */
class LocalizationPopupComponent extends DialogComponent {
  requiredRefs = ['dialog', 'form', 'countryInput'];

  /** @type {string|null} */
  #detectedCountryCode = null;

  connectedCallback() {
    super.connectedCallback();
    this.#initPopup();
  }

  /**
   * Initialize the popup - check if should show and detect location.
   */
  async #initPopup() {
    if (!this.#shouldShowPopup()) {
      return;
    }

    // Detect country via IP in background
    this.#detectCountry();

    // Show popup immediately
    this.showDialog();
  }

  /**
   * Check if the popup should be shown based on stored preference.
   * @returns {boolean}
   */
  #shouldShowPopup() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return true;

      const { timestamp, dismissed } = JSON.parse(stored);

      // Check if preference has expired (14 days)
      const expiryMs = EXPIRY_DAYS * 24 * 60 * 60 * 1000;
      const expired = Date.now() - timestamp > expiryMs;

      if (expired) {
        localStorage.removeItem(STORAGE_KEY);
        return true;
      }

      return false;
    } catch {
      // If there's an error reading storage, show the popup
      return true;
    }
  }

  /**
   * Detect country via IP geolocation API.
   */
  async #detectCountry() {
    try {
      const response = await fetch(IP_API_URL);
      if (!response.ok) return;

      const data = await response.json();
      const detectedCode = data.country_code;

      if (!detectedCode) return;

      // Check if detected country is in available countries
      const availableCountries = this.dataset.countries?.split(',') || [];
      if (!availableCountries.includes(detectedCode)) return;

      this.#detectedCountryCode = detectedCode;

      // Find and highlight the detected country
      const countryItem = this.refs.countryItems?.find(
        (item) => item.dataset.value === detectedCode
      );

      if (countryItem && this.refs.recommendation && this.refs.detectedCountryName) {
        // Show recommendation
        const countryName = countryItem.querySelector('.localization-popup__country-name')?.textContent;
        this.refs.detectedCountryName.textContent = countryName || detectedCode;
        this.refs.recommendation.hidden = false;

        // Pre-select the detected country if it's not already selected
        const currentCountry = this.dataset.currentCountry;
        if (detectedCode !== currentCountry) {
          this.selectCountry(detectedCode);

          // Scroll the detected country into view
          countryItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    } catch {
      // Silently fail - popup still works without IP detection
    }
  }

  /**
   * Select a country and update the UI.
   * @param {string} countryCode - The ISO country code
   */
  selectCountry(countryCode) {
    const { countryItems, countryInput } = this.refs;

    if (!countryItems || !countryInput) return;

    // Update hidden input
    countryInput.value = countryCode;

    // Update aria-current on all items
    countryItems.forEach((item) => {
      const isSelected = item.dataset.value === countryCode;
      item.setAttribute('aria-current', isSelected ? 'true' : 'false');
    });
  }

  /**
   * Filter country list items based on search input.
   */
  filterCountries() {
    const query = this.refs.searchInput?.value.toLowerCase().trim() || '';
    const { countryItems, noResults } = this.refs;

    if (!countryItems) return;

    let visibleCount = 0;

    countryItems.forEach((item) => {
      const name = item.querySelector('.localization-popup__country-name')?.textContent.toLowerCase() || '';
      const currency = item.querySelector('.localization-popup__currency')?.textContent.toLowerCase() || '';
      const matches = name.includes(query) || currency.includes(query);
      item.hidden = !matches;
      if (matches) visibleCount++;
    });

    if (noResults) {
      noResults.hidden = visibleCount > 0;
    }
  }

  /**
   * Handle keyboard navigation on country items.
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const target = event.currentTarget;
      if (target instanceof HTMLElement && target.dataset.value) {
        this.selectCountry(target.dataset.value);
      }
    }
  }

  /**
   * Close dialog and save preference to localStorage.
   */
  closeAndSavePreference = () => {
    this.#savePreference(true);
    this.closeDialog();
  };

  /**
   * Save user preference to localStorage.
   * @param {boolean} dismissed - Whether user explicitly dismissed without changing
   */
  #savePreference(dismissed = false) {
    try {
      const preference = {
        timestamp: Date.now(),
        dismissed,
        country: this.refs.countryInput?.value || null,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preference));
    } catch {
      // Silently fail if localStorage is not available
    }
  }

  /**
   * Override showDialog to add form submission handling.
   */
  showDialog() {
    super.showDialog();

    // Listen for form submission
    this.refs.form?.addEventListener('submit', this.#handleFormSubmit);
  }

  /**
   * Handle form submission - save preference and let form submit naturally.
   * @param {Event} event
   */
  #handleFormSubmit = (event) => {
    // Save preference before form submits (which causes page reload)
    this.#savePreference(false);
  };

  /**
   * Override closeDialog to clean up event listeners.
   */
  closeDialog = async () => {
    this.refs.form?.removeEventListener('submit', this.#handleFormSubmit);
    await super.closeDialog();
  };
}

if (!customElements.get('localization-popup-component')) {
  customElements.define('localization-popup-component', LocalizationPopupComponent);
}
